import { CommonModule } from '@angular/common';
import { environment } from '@env';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { firstValueFrom as firstValue } from 'rxjs';
import { isColorFeature } from '@utils';
import { ActiveStatusEnum, ProductTypeConfigProduct } from '@models';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { OfferingColorImagesDto, OfferingImageApiService } from '@core';

export interface OfferingPreviewParams {
  productTypeConfigId: string;
  /** Shown in the header; the config name a buyer would recognise, e.g. WEN11-7815-41. */
  configName: string;
  productTypeName: string;
  productKey: string | null;
}

/**
 * Stands in for the color of a style that declares none. Parenthesised so it cannot collide
 * with a real color name a supplier might publish, and readable if it ever reaches a screen.
 */
const NO_DECLARED_COLOR = '(no declared color)';

/**
 * A feed URL is already absolute. Proximity's own byte endpoint comes back relative, because
 * the UI and the API are on different ports in development and a root-relative path would hit
 * the UI instead.
 */
function absolute(url: string | null | undefined): string | null {
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? url : environment.apiUrl + url;
}

interface ColorOption {
  name: string;
  /** Sizes this color is actually offered in, smallest first. */
  sizes: string[];
  /** Main supplier image, or null where nothing is staged for this color. */
  imageUrl: string | null;
  /** Small image for the chip. Falls back to the main image when no swatch is published. */
  swatchUrl: string | null;
  /** SanMar's catalogue token, shown so a mismatch is visible rather than silent. */
  supplierColorCode: string | null;
  /**
   * "Feed", "Stored", or null. Stored means Proximity's own byte copy of a STYLE image, shown
   * because the supplier published nothing for this color - the same picture for every color.
   */
  imageSource: string | null;
  /** Approximate Pantone, used to tint the chip when no swatch image exists. */
  pms: string | null;
  /** "StandardColorName" or "ColorCode" — which field carried the match. */
  matchedOn: string | null;
  /**
   * Every other view the supplier publishes for this color - side, three-quarter, flat lays,
   * and anything the feed sent without a label. The main image is NOT repeated here.
   */
  additionalUrls: string[];
  /** Set when several supplier codes shared one standard name; the server picked one. */
  ambiguousCodes: string | null;
}

/**
 * "Show Offering" - proof of concept for the supplier-image and color-swatch approach.
 *
 * Shows what a program actually offers for one product configuration, the way a buyer would
 * meet it: color chips, a size list that reflects what each color is really available in,
 * and a main image that changes with the color.
 *
 * The cascade runs both ways, which is the point of the exercise:
 *   - pick a color  -> the size list narrows to that color's sizes
 *   - pick a size    -> the chips narrow to colors offered in that size
 *
 * Only products the program would actually let someone order are drawn - see isAvailable,
 * which asks the same question the order dialog asks, so a color this preview shows is a color
 * a buyer can really pick.
 *
 * Color and size come from live Proximity data (ProductTypeConfigProducts and their
 * ProductFeatureChoices). Images come from StagedSupplierImages through
 * Supplier/OfferingImages, which returns only the colors this ProductType declares.
 *
 * MATCHING IS ON StandardColorName, NOT ColorCode. SanMar's product-info service returns a
 * 14-character catalogue token - "SndDune/Ld", "Biscuit/Bk" - which matches almost nothing a
 * person typed. Its PromoStandards service returns the same color written out, and that is
 * what a Uniformax color matches. Style 256 is the proof: three of three on the standard
 * name, zero of three on the token. The server falls back to the token, and reports which
 * field carried the match so a mismatch stays visible.
 */
@Component({
  selector: 'prox-offering-preview-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, SelectModule, ProgressSpinnerModule],
  templateUrl: './offering-preview.dialog.html',
  styles: [
    `
      .chip {
        width: 46px;
        height: 46px;
        border-radius: 6px;
        overflow: hidden;
        cursor: pointer;
        background-color: var(--surface-200);
      }
      .chip img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .chip-selected {
        outline: 3px solid var(--p-primary-color);
        outline-offset: 2px;
      }
      .chip-unavailable {
        opacity: 0.25;
        cursor: not-allowed;
      }
      .main-image {
        max-height: 340px;
        object-fit: contain;
      }
    `,
  ],
})
export class OfferingPreviewDialogComponent implements OnInit {
  private dbQueryService = inject(AccountDbQueryService);
  private imageApi = inject(OfferingImageApiService);
  public config = inject<DynamicDialogConfig<OfferingPreviewParams>>(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);

  params!: OfferingPreviewParams;

  loading = true;
  loadError: string | null = null;

  /** Everything the config offers, one entry per color. */
  colors: ColorOption[] = [];
  /** Every size offered by any color, used for the size dropdown when no color is picked. */
  allSizes: string[] = [];
  /** Label of the non-color feature, taken from the data rather than assumed to be "Size". */
  sizeFeatureName = 'Size';

  selectedColor: ColorOption | null = null;

  /**
   * The view currently in the big pane. Null means the color's main image.
   *
   * Held separately from selectedColor so picking a different color always returns to that
   * color's main shot rather than leaving a stale side view on screen.
   */
  selectedView: string | null = null;
  selectedSize: string | null = null;

  /** Tooltip on the first thumbnail, which is always the color's main shot. */
  readonly mainViewLabel = ' - main view';

  /** How many offered colors resolved to a supplier image. Shown in the header. */
  matchedCount = 0;

  /**
   * Whether any catalog feed covers this manufacturer. False for 76 of our 78.
   *
   * Everything this dialog says about matching - how many colors resolved, the supplier's own
   * code, that the supplier publishes no color of this name - is meaningless without a feed,
   * and reads as a reconciliation failure that cannot happen. When false the dialog says none
   * of it and simply shows the picture we hold.
   */
  isFeedEnabled = false;

  /** True when any product actually declared a color, as opposed to the invented single group. */
  hasDeclaredColor = false;

  /**
   * Whether there is a color worth asking about.
   *
   * A declared color always counts. An invented one counts only where a feed covers the
   * manufacturer, because there the single group carries the supplier's real color name -
   * Navy, for a Red Kap style. With no feed and no color feature there is no color dimension
   * at all, and a dropdown holding "One color" is a question with one answer.
   */
  get hasColorChoice(): boolean {
    return this.hasDeclaredColor || this.isFeedEnabled;
  }
  /** Set when the images could not be fetched. The offering still renders without them. */
  imageError: string | null = null;

  static async openDialog(dialogService: DialogService, data: OfferingPreviewParams) {
    const ref = dialogService.open(OfferingPreviewDialogComponent, {
      closable: true,
      draggable: true,
      modal: true,
      width: '900px',
      header: `Offering - ${data.configName}`,
      data,
    });
    return firstValueFrom(ref.onClose);
  }

  async ngOnInit(): Promise<void> {
    this.params = this.config.data!;
    try {
      await this.load();
    } catch (e: any) {
      this.loadError = e?.message ?? String(e);
    } finally {
      this.loading = false;
    }
  }

  private async load(): Promise<void> {
    const ptc = await this.dbQueryService.getProductTypeConfig(this.params.productTypeConfigId);
    if (!ptc) {
      this.loadError = 'Configuration not found.';
      return;
    }

    // Identify which feature is color. Feature names are supplier free text, so match the way
    // the order dialog already does rather than testing for the literal word "Size".
    const ptFeatures = ptc.pricedProductType?.productType?.productTypeFeatures ?? [];
    const colorFeatureIds = new Set(
      ptFeatures.filter((ptf) => isColorFeature(ptf.feature?.name ?? '')).map((ptf) => ptf.featureId),
    );
    const sizeFeature = ptFeatures.find((ptf) => !colorFeatureIds.has(ptf.featureId));
    this.sizeFeatureName = sizeFeature?.feature?.name ?? 'Size';

    // Resolve choice ids explicitly from the feature choices the query DOES expand, rather than
    // reading pfc.featureChoice - that nav prop is not in the expand and would only resolve if
    // the entity happened to be in the Breeze cache.
    const choiceById = new Map<string, { featureId: string; validValue: string; displayOrder: number }>();
    for (const ptf of ptFeatures) {
      for (const fc of ptf.feature?.featureChoices ?? []) {
        choiceById.set(fc.id, {
          featureId: ptf.featureId,
          validValue: fc.validValue,
          displayOrder: fc.displayOrder,
        });
      }
    }

    // Walk the products this config actually offers and collect color -> sizes.
    //
    // SIZES CARRY THEIR CATALOG ORDER IN displayOrder - "Alpha size" runs XXS 10, XS 20, S 30 ...
    // 3X 80, 4X 90, and it is set on every one of the 722 size choices. Collecting them in the
    // order the products happened to arrive produced "XL, M, 4X, S, L, XS, 2X, 3X", which a
    // reader takes for a range that stops at 3X - so a size the program really offers looked
    // missing. Sorted the same way the order dialog sorts its feature choices: displayOrder,
    // then the name to break a tie.
    const byColor = new Map<string, Set<string>>();
    const sizeRank = new Map<string, number>();

    for (const ptcp of ptc.productTypeConfigProducts ?? []) {
      if (!this.isAvailable(ptcp)) continue;
      const choices = ptcp.product?.productFeatureChoices ?? [];
      let colorName: string | null = null;
      let sizeName: string | null = null;
      let sizeDisplayOrder = 0;

      for (const pfc of choices) {
        const fc = choiceById.get(pfc.featureChoiceId);
        if (!fc) continue;
        if (colorFeatureIds.has(fc.featureId)) {
          colorName = fc.validValue;
        } else if (!sizeFeature || fc.featureId === sizeFeature.featureId) {
          sizeName = fc.validValue;
          sizeDisplayOrder = fc.displayOrder;
        }
      }

      // A PRODUCT WITH NO COLOR DIMENSION IS NOT A PRODUCT WITH NOTHING TO SHOW.
      //
      // This used to skip any product that yielded no color, which emptied the whole dialog -
      // no color to choose, no sizes under it, and "nothing in this configuration is available
      // right now" on a configuration that is entirely available. Invisible while every
      // supplier here varied style by color. Most uniform suppliers do not: the style IS the
      // color, so those products carry a size feature and nothing else.
      //
      // Such products are collected under one unnamed group, which is named further down from
      // whatever the supplier feed calls the style's color.
      if (!sizeName) continue;
      const colorKey = colorName ?? NO_DECLARED_COLOR;
      if (!byColor.has(colorKey)) byColor.set(colorKey, new Set<string>());
      byColor.get(colorKey)!.add(sizeName);
      sizeRank.set(sizeName, sizeDisplayOrder);
    }

    const sizeOrder = [...sizeRank.keys()].sort(
      (a, b) => (sizeRank.get(a) ?? 0) - (sizeRank.get(b) ?? 0) || a.localeCompare(b),
    );

    this.allSizes = sizeOrder;

    // Images are fetched per ProductType, not per style string. The server resolves the style
    // itself, so a ProductKey carrying a stray newline - a dozen of them do - cannot be
    // mis-keyed here. A failure is non-fatal: the offering is still worth showing without
    // pictures, and swallowing the color and size data with it would be the worse trade.
    const productTypeId = ptc.pricedProductType?.productType?.id;
    const images = new Map<string, OfferingColorImagesDto>();
    if (productTypeId) {
      try {
        const rows = await firstValue(this.imageApi.getForProductType(productTypeId));
        for (const r of rows) images.set(r.ColorName.trim().toLowerCase(), r);
      } catch (e: any) {
        this.imageError = e?.message ?? String(e);
      }
    }

    // The single image row the feed staged, used only to name the unnamed group. Safe because
    // a style with no declared color has exactly one color in the feed - it is one garment.
    const onlyImage = images.size === 1 ? [...images.values()][0] : null;

    this.colors = [...byColor.entries()]
      .map(([key, sizes]) => {
        const undeclared = key === NO_DECLARED_COLOR;
        // Named from the feed where we have it. Falling back to "One color" rather than
        // leaving it blank, so the chip and the caption still read as something deliberate.
        const name = undeclared ? (onlyImage?.ColorName?.trim() || 'One color') : key;
        const img = undeclared ? onlyImage : images.get(key.trim().toLowerCase());
        return {
          name,
          sizes: sizeOrder.filter((s) => sizes.has(s)),
          imageUrl: absolute(img?.MainImageUrl),
          // A chip wants the small asset, but SanMar does not publish a swatch for every
          // color. Fall back through thumbnail to the main image rather than showing n/a
          // when a picture does exist.
          swatchUrl: absolute(img?.SwatchUrl ?? img?.ThumbnailUrl ?? img?.MainImageUrl),
          supplierColorCode: img?.SupplierColorCode ?? null,
          pms: img?.ApproximatePms ?? null,
          matchedOn: img?.MatchedOn ?? null,
          ambiguousCodes: img?.AmbiguousCodes ?? null,
          additionalUrls: img?.AdditionalUrls ?? [],
          imageSource: img?.MainImageSource ?? null,
        } as ColorOption;
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    // A supplier code is the feed's own match. A picture alone is not: an unmatched color now
    // carries the stored style image, which must not count as matched to supplier images.
    this.matchedCount = this.colors.filter((c) => c.supplierColorCode).length;
    this.isFeedEnabled = [...images.values()].some((r) => r.IsFeedEnabled);
    this.hasDeclaredColor = [...byColor.keys()].some((k) => k !== NO_DECLARED_COLOR);

    this.selectedColor = this.colors[0] ?? null;
    this.selectedSize = null;
  }

  /**
   * The same test the order dialog uses to decide which products a buyer may pick:
   * ProductTypeConfigProduct.activeStatusId AND Product.isOrderable(). See job-order-detail-dialog,
   * which builds its product list the same way.
   *
   * The link alone is not enough. CTY1-8001-581, 2026-11-02: an Inactive Orange/Black product on an
   * Active link showed here as a color, and the order dialog correctly did not offer it.
   *
   * This used to ask StatusService for the rolled-up status icon, which walks the whole chain
   * up to the Account. An Account on Hold therefore marked every product unavailable, and this
   * dialog showed "nothing in this configuration is available right now" for every single
   * configuration under it - on PGCPS-Warehouse, all of them. Hold is also the state you put an
   * Account in to work on it, so the preview went blank exactly when it was wanted.
   *
   * A preview of the order screen has to answer the question the order screen answers. It is
   * not the place to re-state that the Account is on Hold; the page above already says so.
   */
  private isAvailable(ptcp: ProductTypeConfigProduct): boolean {
    return ptcp.activeStatusId === ActiveStatusEnum.Active && !!ptcp.product?.isOrderable();
  }

  /** Sizes to offer right now: this color's sizes, or every size when no color is chosen. */
  get availableSizes(): string[] {
    return this.selectedColor ? this.selectedColor.sizes : this.allSizes;
  }

  /** A color is selectable only if it comes in the size the user has already chosen. */
  isColorAvailable(c: ColorOption): boolean {
    return !this.selectedSize || c.sizes.includes(this.selectedSize);
  }

  onSelectColor(c: ColorOption): void {
    if (!this.isColorAvailable(c)) return;
    this.selectedColor = c;
    // Back to the main shot. A side view left over from the previous color would be a picture
    // of a different garment.
    this.selectedView = null;
    // Drop a size the newly chosen color does not come in, rather than showing an impossible pair.
    if (this.selectedSize && !c.sizes.includes(this.selectedSize)) {
      this.selectedSize = null;
    }
  }

  onSelectSize(): void {
    // Picking a size narrows the chips. If the current color does not come in it, let go of it.
    if (this.selectedColor && this.selectedSize && !this.selectedColor.sizes.includes(this.selectedSize)) {
      this.selectedColor = null;
    }
  }

  clearSize(): void {
    this.selectedSize = null;
  }

  close(): void {
    this.ref.close();
  }
}
